import React from "react";
import { useSelector } from "react-redux";
import { RootState } from "../../store";
import { hasPermission, hasRole, hasReportPermission } from "../../utils/permissions";

interface PermissionGuardProps {
  module: string;
  action: string;
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

interface RoleGuardProps {
  roles: string[];
  children: React.ReactNode;
  fallback?: React.ReactNode;
}

/**
 * PermissionGuard - Renders children only if user has the specified permission
 */
export const PermissionGuard: React.FC<PermissionGuardProps> = ({
  module,
  action,
  children,
  fallback = null,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const userPermissions = user?.permissions;
  const userRole = user?.role;
  const userRoles = user?.roles; // RBAC roles array

  // Super admin has all permissions (check both old role field and new RBAC roles)
  const isSuperAdmin = userRole === "super_admin" || (userRoles && userRoles.includes("super_admin"));
  if (isSuperAdmin) {
    return <>{children}</>;
  }

  // All other users (including admin) rely on RBAC system permissions
  const hasAccess = hasPermission(userPermissions, module, action);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * RoleGuard - Renders children only if user has one of the specified roles
 */
export const RoleGuard: React.FC<RoleGuardProps> = ({
  roles,
  children,
  fallback = null,
}) => {
  const user = useSelector((state: RootState) => state.auth.user);
  const userRole = user?.role || "";

  const hasAccess = hasRole(userRole, roles);

  if (!hasAccess) {
    return <>{fallback}</>;
  }

  return <>{children}</>;
};

/**
 * usePermission - Hook to check if user has a specific permission
 */
export const usePermission = (module: string, action: string): boolean => {
  const user = useSelector((state: RootState) => state.auth.user);
  const userPermissions = user?.permissions;
  const userRole = user?.role;
  const userRoles = user?.roles; // RBAC roles array

  // Super admin has all permissions (check both old role field and new RBAC roles)
  const isSuperAdmin = userRole === "super_admin" || (userRoles && userRoles.includes("super_admin"));
  if (isSuperAdmin) {
    return true;
  }

  // All other users (including admin) rely on RBAC system permissions
  return hasPermission(userPermissions, module, action);
};

/**
 * useRole - Hook to check if user has one of the specified roles
 */
export const useRole = (roles: string[]): boolean => {
  const user = useSelector((state: RootState) => state.auth.user);
  const userRole = user?.role || "";

  return hasRole(userRole, roles);
};

/**
 * useReportPermission - Hook to check if user has permission to access a specific report
 * @param reportId - Report ID
 * @param action - Action name (default: 'read')
 */
export const useReportPermission = (
  reportId: string, 
  action: string = 'read'
): boolean => {
  const user = useSelector((state: RootState) => state.auth.user);
  const userPermissions = user?.permissions;
  const userRole = user?.role;
  const userRoles = user?.roles;

  // Super admin has all permissions
  const isSuperAdmin = userRole === "super_admin" || (userRoles && userRoles.includes("super_admin"));
  if (isSuperAdmin) {
    return true;
  }

  return hasReportPermission(userPermissions, reportId, action);
};
